<script setup lang="ts">
import {
	useRoute,
	useRouter
} from "vue-router";
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch
} from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import FileBlock from "@/components/main/project-block-page/file-block/FileBlock.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppSelect from "@/components/ui-kit/select/AppSelect.vue";
import ProjectAddClientDropdown from "@/components/main/project-block-page/dropdowns/ProjectAddClientDropdown.vue";
import ProjectAddWorkerDropdown from "@/components/main/project-block-page/dropdowns/ProjectAddWorkerDropdown.vue";
import ProjectAddRoleDropdown from "@/components/main/project-block-page/dropdowns/ProjectAddRoleDropdown.vue";
import DeleteProjectConfirmDropdown from "@/components/main/project-block-page/dropdowns/DeleteProjectConfirmDropdown.vue";
import ProjectInfoModal from "@/components/main/project-block-page/modals/ProjectInfoModal.vue";
import ProjectFinanceSection from "@/components/main/project-block-page/finance/ProjectFinanceSection.vue";
import ProjectFinanceSummary from "@/components/main/project-block-page/finance/ProjectFinanceSummary.vue";
import ParametersSection from "@/components/main/shared/ParametersSection.vue";
import { V2ParameterAttachmentType } from "@/models/etc/parameter-types";
import StageBusinessLogicModal from "@/components/main/project-block-page/modals/StageBusinessLogicModal.vue";
import ProjectClientsExpandModal from "@/components/main/project-block-page/modals/ProjectClientsExpandModal.vue";
import ProjectFinanceExpandModal from "@/components/main/project-block-page/modals/ProjectFinanceExpandModal.vue";
import { useOverflowDetection } from "@/composables/useOverflowDetection";
import AppScrollTopButton from "@/components/ui-kit/button/AppScrollTopButton.vue";
import { useScrollTopButton } from "@/composables/useScrollTopButton";
import { useBreakpoints } from "@/composables/useBreakpoints";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import FileInput from "@/components/file/FileInput.vue";
import { projectPageStore } from "@/store/project-page";
import { favouritesStore } from "@/store/favourites";
import { searchStore } from "@/store/search";
import { TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import moment from "moment/moment";
import { workerStore } from "@/store/worker";
import { buildAbsoluteClientUrl } from "@/utils/urlHelpers";
import { copyTextToClipboard } from "@/utils/clipboard";
import { i18n } from "@/i18n";

const projectStore = projectPageStore()
const favourStore = favouritesStore()
const workerStorage = workerStore();
const searchStorage = searchStore()

const route = useRoute()
const router = useRouter()
const { isDesktop } = useBreakpoints()

const $projectInfoModal = ref<InstanceType<typeof ProjectInfoModal> | null>(null)
const $stageBusinessLogicModal = ref<InstanceType<typeof StageBusinessLogicModal> | null>(null)
const $deleteProject = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $projectNameInput = ref<InstanceType<typeof FileInput> | null>(null)
const $stageAccessTooltip = ref<typeof AppTooltip | null>(null)
const $addTag = ref<typeof AppResizableDropdown | null>(null)
const $addClient = ref<typeof AppResizableDropdown | null>(null)
const $addWorker = ref<typeof AppResizableDropdown | null>(null)

const isProjectNameEdited = ref(false)
const editedProjectName = ref("")
const isProjectNameNew = ref(false)

// Меню добавления финансов открывается из "+" в шапке блока; сам раздел рендерится телом блока
const financeAddOpen = ref(false)
const financeAddAnchor = ref<HTMLElement | null>(null)
// Меню добавления параметра открывается из "+" в шапке блока «Параметры»
const parametersAddOpen = ref(false)
const parametersAddAnchor = ref<HTMLElement | null>(null)
const $addWorkerRoles = ref<Record<number, any>>({})
const $addClientRoles = ref<Record<number, any>>({})

const isProjectLoading = computed(() => projectStore.projectLoading)
const isFavouritesLoading = computed(() => favourStore.isFavouritesLoading)
const projectId = computed(() => parseInt(route.query.id as string))
const project = computed(() => projectStore.openedProject)

const $projectPage = ref<HTMLElement | null>(null)
const { isScrollTopEnabled, scrollToTop } = useScrollTopButton(
	$projectPage,
	'.project-page__header',
	() => project.value?.Id
)

const isAdmin= workerStorage.workerProfile?.IsAdmin;

const hasAnyRestrictedAccess = computed(() => {
  const p = projectStore.project;
  if (!p) return false;

  if (isAdmin) return false;

  return (
    p.AccessLevel === 0 ||
    p.NameAccess === 0 ||
    p.StageAccess === 0 ||
    p.FinanceAccess === 0 ||
    p.AttachedClientsAccess === 0 ||
    p.AttackedWorkersAccess === 0 ||
    p.ParameterAccess === 0 ||
    p.FileAccess === 0 ||
    p.DeleteAccess === 0
  );
});

// --- Раскрытие блока «Клиенты» на весь экран (модалка) ---
// Стрелка показывается только когда тело блока переполнено и появился скролл.
const $clientsBody = ref<HTMLElement | null>(null)
const $clientsModal = ref<InstanceType<typeof ProjectClientsExpandModal> | null>(null)

const { isOverflowing: isClientsOverflowing } = useOverflowDetection(
	$clientsBody,
	() => project.value?.AttachedClients?.length ?? 0,
)

// --- Раскрытие блока «Финансы» на весь экран (модалка) ---
// Скролл-контейнер тут не обёртка, а корень самой секции, поэтому элемент берём из инстанса компонента.
const $financeSection = ref<InstanceType<typeof ProjectFinanceSection> | null>(null)
const $financeBody = ref<HTMLElement | null>(null)
const $financeModal = ref<InstanceType<typeof ProjectFinanceExpandModal> | null>(null)
watch($financeSection, (section) => {
	$financeBody.value = (section?.$el as HTMLElement) ?? null
}, { immediate: true })

const { isOverflowing: isFinanceOverflowing } = useOverflowDetection(
	$financeBody,
	() => (project.value?.Bills?.length ?? 0)
		+ (project.value?.StorageItemExpenditureMovements?.length ?? 0),
)

const fetchProject = async () => {
	await projectStore.getProject(projectId.value)
}

const toggleFavourite = async () => {
	try {
		if (!isFavouritesLoading.value) {
			if (!project.value?.Favorite) {
				await favourStore.addFavourites(projectId.value, 1)
			} else {
				await favourStore.removeFavourites(projectId.value, 1)
			}
			projectStore.updateCurrentProjectFavouriteState(!project.value?.Favorite)
		}
	} catch (error) {
		console.error(error)
	}
}

const $shareTooltip = ref<typeof AppTooltip | null>(null)

const SHARE_TOOLTIP_KEY = 'ui_share'
const SHARE_COPIED_KEY = 'link_copied_to_clipboard'
const SHARE_COPIED_DELAY = 3000

const shareTooltipKey = ref(SHARE_TOOLTIP_KEY)
const shareTooltipText = computed(() => String(i18n.global.t(shareTooltipKey.value)))

let shareTooltipTimer: ReturnType<typeof setTimeout> | null = null

const onCopyProjectRoute = async () => {
	if (Number.isNaN(projectId.value)) {
		return
	}

	const copied = await copyTextToClipboard(
		buildAbsoluteClientUrl(`main?page=project&id=${projectId.value}`)
	)
	if (!copied) {
		return
	}

	if (shareTooltipTimer) {
		clearTimeout(shareTooltipTimer)
	}
	shareTooltipKey.value = SHARE_COPIED_KEY
	$shareTooltip.value?.showByClick()
	shareTooltipTimer = setTimeout(() => {
		shareTooltipKey.value = SHARE_TOOLTIP_KEY
		shareTooltipTimer = null
	}, SHARE_COPIED_DELAY)
}

onBeforeUnmount(() => {
	if (shareTooltipTimer) {
		clearTimeout(shareTooltipTimer)
	}
})

const getCreatedDate = (date: string | Date) => moment(date).format("DD.MM.YYYY");

const editProjectName = () => {
	editedProjectName.value = project.value?.Name ?? ""
	isProjectNameEdited.value = true
	nextTick(() => $projectNameInput.value?.focus())
}

// Проект создан из Поиска: сразу открываем переименование, чтобы ввод заменил название по умолчанию.
const startPendingNameEdit = () => {
	const req = searchStorage.pendingNameEdit
	if (!req || req.type !== 'project' || req.id !== projectId.value) {
		return
	}
	searchStorage.clearPendingNameEdit()
	if (!isDesktop.value || project.value?.NameAccess !== 2) {
		return
	}
	isProjectNameNew.value = true
	editProjectName()
}

const saveProjectName = async () => {
	isProjectNameNew.value = false
	isProjectNameEdited.value = false
	const trimmed = editedProjectName.value.trim()
	if (!trimmed || trimmed === project.value?.Name) return

	try {
		await projectStore.renameCurrentProject(trimmed)
	} catch (error) {
		console.error(error)
	}
}

const openProjectInfoModal = () => {
	$projectInfoModal.value?.onOpen(projectId.value, (project.value?.Tags ?? []) as any)
}

const openStageBusinessLogicModal = () => {
	if (project.value?.StageAccess === 0) return
	if (project.value?.StageId == null) return
	$stageBusinessLogicModal.value?.onOpen(project.value.StageId)
}

const stageItems = computed(() => {
	const items = (project.value?.Stages ?? []).map((s: any) => ({
		id: s.Id,
		label: s.Name,
		disabled: s.IsAvailable === false || s.IsDisabled === true,
	}))
	return [
		...items.filter(i => !i.disabled),
		...items.filter(i => i.disabled),
	]
})

const onSelectStage = async (value: string | number | (string | number)[] | null) => {
	if (project.value?.StageAccess !== 2) return
	if (value == null || Array.isArray(value)) return

	const stageId = Number(value)
	if (stageId === project.value?.StageId) return

	try {
		await projectStore.changeProjectStage(stageId)
	} catch (e) {
		console.error(e)
	}
}

const onStageSelectClick = () => {
	if (project.value?.StageAccess === 1) {
		$stageAccessTooltip.value?.showByClick()
	}
}

const projectTagsModel = computed<TagWithFrequency[]>(
	() => (project.value?.Tags ?? []) as unknown as TagWithFrequency[]
)

const onSelectorChange = async (nextTags: TagWithFrequency[]) => {
	const prevIds = new Set((project.value?.Tags ?? []).map((t: any) => t.Id))
	const nextIds = new Set(nextTags.map(t => t.Id))

	const added = nextTags.filter(t => !prevIds.has(t.Id))
	const removedIds = [...prevIds].filter(id => !nextIds.has(id))

	try {
		for (const tag of added) await projectStore.attachTagToProject({ Name: tag.Name })
		for (const id of removedIds) await projectStore.detachTagFromProject(id as number)
	} catch (e) {
		console.error(e)
	}
}

const onCreateTag = async (name: string) => {
	try {
		await projectStore.attachTagToProject({ Name: name })
	} catch (e) {
		console.error(e)
	}
}

const onDetachTag = async (tagId: number) => {
	try {
		await projectStore.detachTagFromProject(tagId)
	} catch (e) {
		console.error(e)
	}
}

const closeAddClientDropdown = () => {
	$addClient.value?.close()
}

const onDetachClient = async (id: number) => {
	try {
		await projectStore.detachClientFromProject(id)
	} catch (e) {
		console.error(e)
	}
}

const closeAddWorkerDropdown = () => {
	$addWorker.value?.close()
}

const onDetachWorker = async (id: number) => {
	try {
		await projectStore.detachWorkerFromProject(id)
	} catch (e) {
		console.error(e)
	}
}

const closeAddWorkerRoleDropdown = (awId: number) => {
	$addWorkerRoles.value[awId]?.close()
}

const closeAddClientRoleDropdown = (acId: number) => {
	$addClientRoles.value[acId]?.close()
}

const onDetachWorkerRole = async (workerId: number, roleId: number) => {
	try {
		await projectStore.detachRoleFromWorker(workerId, roleId)
	} catch (e) {
		console.error(e)
	}
}

const onDetachClientRole = async (clientId: number, roleId: number) => {
	try {
		await projectStore.detachRoleFromClient(clientId, roleId)
	} catch (e) {
		console.error(e)
	}
}

const onConfirmDelete = async () => {
	if (isProjectLoading.value) return
	try {
		await projectStore.deleteProject()
		await router.push({
			path: '/',
			query: {
				page: 'chat',
				mode: 'list',
			},
		})
	} catch (error) {
		console.error(error)
		$deleteProject.value?.close()
	}
}

watch(
	() => route.query.id,
	async (newVal, oldVal) => {
		if (newVal !== oldVal) {
			await fetchProject()
			startPendingNameEdit()
		}
	}
)

onMounted(async () => {
	await fetchProject()
	startPendingNameEdit()
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))
</script>

<template>
	<div class="project-page" ref="$projectPage">
		<app-loader v-if="isProjectLoading" :style="loaderStyle"/>
		<app-scroll-top-button
			class="project-page__scroll-top"
			:enabled="isScrollTopEnabled"
			@on-click="scrollToTop"
		/>
		<div class="project-page__header">
			<div class="project-page__header-person">
				<div class="project-page__header-person-name">
					{{ project?.Creator?.Name || $t('user_label') }}
				</div>
				<div class="project-page__header-person-date">
					{{ project ? getCreatedDate(project.CreateTime) : '' }}
				</div>
				<app-resizable-dropdown
					v-if="project?.CanBeDeleted && projectStore.project?.DeleteAccess!==0"
					ref="$deleteProject"
					position="right"
					centered-on-mobile
					:close-by-click-outside="true"
					name="$deleteProject"
				>
					<template #activator>
						<app-tooltip :text="$t('remove')">
							<template #activator>
								<app-icon
									class="project-page__header-person-icon"
									name="delete-outline"
									width="18px"
									color="#979797"
									hover-color="#BA4C4C"
									cursor="pointer"
								/>
							</template>
						</app-tooltip>
					</template>
					<delete-project-confirm-dropdown
						:loading="isProjectLoading"
						@cancel="$deleteProject?.close()"
						@confirm="onConfirmDelete"
					/>
				</app-resizable-dropdown>
			</div>
			<div class="project-page__header-options">
				<app-tooltip :text="$t(project?.Favorite ? 'favourite_remove' : 'add_to_favorites')">
					<template #activator>
						<app-icon
							class="project-page__header-options-icon"
							name="favourite"
							width="18px"
							:color="project?.Favorite ? '#D05A4F' : '#979797'"
							:hover-color="project?.Favorite ? '#979797' : '#D05A4F'"
							cursor="pointer"
							@click="toggleFavourite"
						/>
					</template>
				</app-tooltip>
				<app-tooltip
					ref="$shareTooltip"
					:text="shareTooltipText"
				>
					<template #activator>
						<app-icon
							class="project-page__header-options-icon"
							:style="{ marginTop: '1px' }"
							name="share"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="onCopyProjectRoute"
						/>
					</template>
				</app-tooltip>
				<app-resizable-dropdown
					v-if="projectStore.project?.AccessLevel!==0"
					ref="$addTag"
					position="right"
					:close-by-click-outside="true"
					:max-width="640"
					:max-height="460"
					:mobile-title="$t('choose_tags')"
					name="$addTagProjectPage"
				>
					<template #activator>
						<app-tooltip :text="$t('file_add_tag')">
							<template #activator>
								<app-icon
									class="project-page__header-options-icon"
									name="hash"
									width="18px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
								/>
							</template>
						</app-tooltip>
					</template>
					<app-tag-selector
						:model-value="projectTagsModel"
						:entity-type="TagEntityType.Project"
						mode="select"
						allow-create
						@update:model-value="onSelectorChange"
						@create-tag="onCreateTag"
						@close="$addTag?.close()"
					/>
				</app-resizable-dropdown>
				<app-icon
					v-else
					class="project-page__header-options-icon"
					name="hash"
					width="18px"
					color="#D2D2D2"
				/>
				<div
					v-if="project?.Tags?.length"
					class="project-page__header-options-tags"
				>
					<div
						v-for="tag in project.Tags"
						:key="tag.Id"
						class="project-page__header-options-tags-item"
					>
						<div class="project-page__header-options-tags-item-name">
							{{ tag.Name.toLowerCase() }}
						</div>
						<app-tooltip :text="$t('ui_tag_detach')" v-if="projectStore.project?.AccessLevel!==0">
							<template #activator>
								<app-icon
									class="project-page__direction-aw-left-icon"
									:style="{ marginTop: '-2px' }"
									name="close-bold"
									width="12px"
									color="#D2D2D2"
									hover-color="#BA4C4C"
									cursor="pointer"
									@click="onDetachTag(tag.Id)"
								/>
							</template>
						</app-tooltip>
					</div>
				</div>
			</div>
		</div>
		<div class="project-page__header-permition" v-if="hasAnyRestrictedAccess">
			<app-icon
				class="project-page__header-permition-icon"
				name="invalid"
				width="16px"
				color="#979797"
			/>
			<div class="project-page__header-permition-label">
				{{ $t('project_limited_access') }}
			</div>
		</div>
		<div class="project-page__divider" />

		<div class="project-page__project">
			<div class="project-page__project-icon-block">
				<app-tooltip :text="$t('info')" v-if="projectStore.project?.AccessLevel==2 || projectStore.project?.AccessLevel==1">
					<template #activator>
						<app-icon
							class="project-page__project-icon"
							:style="{ marginTop: '-2px' }"
							name="info-circle-outline"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="openProjectInfoModal"
						/>
					</template>
				</app-tooltip>
				<app-icon
				v-else
					class="project-page__project-icon"
					:style="{ marginTop: '-2px' }"
					name="info-circle-outline"
					width="18px"
					color="#D2D2D2"
				/>
			</div>
			<div class="project-page__project-title">
				{{ $t('project_label') }}{{projectStore.project?.NameAccess!==0 ? ':' : ''}}
				<file-input
					v-if="isProjectNameEdited"
					ref="$projectNameInput"
					v-model="editedProjectName"
					:new-el="isProjectNameNew"
					@blur="saveProjectName"
				/>
				<template v-else>
					<div class="project-page__project-name" v-if="projectStore.project?.NameAccess!==0">
						{{ project?.Name || '' }}
					</div>
					<app-tooltip class="project-page__project-edit" :text="$t('edit_label')" v-if="projectStore.project?.NameAccess==2">
						<template #activator>
							<app-icon
								class="project-page__project-icon"
								:style="{ marginTop: '-2px' }"
								name="edit-pencil"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="editProjectName"
							/>
						</template>
					</app-tooltip>
				</template>
			</div>
		</div>
		<div class="project-page__divider" />

		<div class="project-page__stage">
			<div class="project-page__project-icon-block">
				<app-tooltip :text="$t('stage_business_logic')" v-if="projectStore.project?.StageAccess !== 0">
					<template #activator>
						<app-icon
							class="project-page__project-icon"
							:style="{ marginTop: '-2px' }"
							name="info-circle-outline"
							width="18px"
							color="#979797"
							hover-color="#499259"
							cursor="pointer"
							@click="openStageBusinessLogicModal"
						/>
					</template>
				</app-tooltip>
				<app-icon
					v-else
					class="project-page__project-icon"
					:style="{ marginTop: '-2px' }"
					name="info-circle-outline"
					width="18px"
					color="#D2D2D2"
				/>
			</div>
			<div class="project-page__stage-title">
				{{ $t('stage_label') }}{{projectStore.project?.StageAccess!==0 ? ':' : ''}}
				<app-tooltip
					v-if="projectStore.project?.StageAccess !== 0"
					ref="$stageAccessTooltip"
					class="project-page__stage-select-tooltip"
					:text="$t('access_rights_restricted')"
					anchor-align="start"
					show-by-activator-click
					close-on-activator-mouse-leave
				>
					<template #activator>
						<app-select
							class="project-page__stage-select"
							:model-value="project?.StageId ?? null"
							:items="stageItems"
							placeholder="—"
							:disabled="project?.StageAccess !== 2"
							@update:model-value="onSelectStage"
							@click="onStageSelectClick"
						/>
					</template>
				</app-tooltip>
			</div>
		</div>
		<div class="project-page__divider" />

		<div class="project-page__direction">
			<div class="project-page__direction-header project-page__direction-header--finance">
				<div class="project-page__direction-header-left">
					<app-tooltip :text="$t('ui_add')" v-if="projectStore.project?.FinanceAccess==2">
						<template #activator>
							<span
								ref="financeAddAnchor"
								class="project-page__direction-header-add-anchor"
								@click="financeAddOpen = true"
							>
								<app-icon
									class="project-page__direction-header-icon"
									name="plus"
									width="15px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
								/>
							</span>
						</template>
					</app-tooltip>
					<app-icon
						v-else
						class="project-page__direction-header-icon"
						name="plus"
						width="15px"
						color="#D2D2D2"
					/>
					<div class="project-page__direction-header-title">
						{{ $t('finances') }}
					</div>
					<app-tooltip
						v-if="projectStore.project?.FinanceAccess !== 0 && isFinanceOverflowing"
						:text="$t('ui_fullscreen')"
					>
						<template #activator>
							<app-icon
								class="project-page__direction-header-icon"
								name="arrow-expand"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="$financeModal?.onOpen()"
							/>
						</template>
					</app-tooltip>
				</div>
				<div
					v-if="project && projectStore.project?.FinanceAccess !== 0"
					class="project-page__direction-header-right"
				>
					<project-finance-summary :project="project"/>
				</div>
			</div>
			<div
				v-if="project && projectStore.project?.FinanceAccess !== 0"
				class="project-page__direction-body"
			>
				<project-finance-section
					ref="$financeSection"
					:project="projectStore.project"
					:project-id="projectId"
					:finance-access="projectStore.project?.FinanceAccess"
					:add-menu-target="financeAddAnchor"
					v-model:add-menu-open="financeAddOpen"
					@changed="fetchProject"
				/>
			</div>
		</div>
		<div class="project-page__divider" />

		<div class="project-page__direction">
			<div class="project-page__direction-header">
				<div class="project-page__direction-header-left">
					<app-resizable-dropdown
						v-if="projectStore.project?.AttachedClientsAccess==2"
						position="right"
						resizable
						:initial-width="400"
						:initial-height="450"
						:min-width="400"
						:max-width="800"
						:min-height="450"
						:max-height="900"
						:mobile-title="$t('choose_clients')"
						ref="$addClient"
						name="$addClient"
					>
						<template #activator>
							<app-tooltip :text="$t('ui_add')">
								<template #activator>
									<app-icon
										class="project-page__direction-header-icon"
										name="plus"
										width="15px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>
						<project-add-client-dropdown
							v-if="project"
							:project-id="project.Id"
							:added-clients="project.AttachedClients"
							show-close-icon
							@close="closeAddClientDropdown"
						/>
					</app-resizable-dropdown>
					<app-icon
						v-else
						class="project-page__direction-header-icon"
						name="plus"
						width="15px"
						color="#D2D2D2"
					/>
					<div class="project-page__direction-header-title">
						{{ $t('clients_label') }}
					</div>
					<app-tooltip
						v-if="isClientsOverflowing"
						:text="$t('ui_fullscreen')"
					>
						<template #activator>
							<app-icon
								class="project-page__direction-header-icon"
								name="arrow-expand"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="$clientsModal?.onOpen()"
							/>
						</template>
					</app-tooltip>
				</div>
			</div>
			<div
				v-if="project?.AttachedClients && project.AttachedClients.length && projectStore.project?.AttachedClientsAccess!==0"
				ref="$clientsBody"
				:class="{
					'project-page__direction-body': true,
					'project-page__direction-body--uc': true
				}"
			>
				<div
					v-for="(ac, index) in project.AttachedClients"
					:key="ac.Id"
					class="project-page__direction-uc"
				>
					<div class="project-page__direction-uc-left">
						<div class="project-page__direction-uc-left-count">{{ index + 1 }}.</div>
						<div class="project-page__direction-uc-left-name">
							<router-link
								class="project-page__direction-uc-left-name-str"
								:to="{ query: { page: 'client', id: ac.Id } }"
							>
								{{ ac.Name }}
							</router-link>
							<span
								v-if="ac.Address"
								class="project-page__direction-uc-left-address"
							>
								({{ ac.Address }})
							</span>
							<app-tooltip :text="$t('remove')" v-if="projectStore.project?.AttachedClientsAccess==2 && project.AttachedClients.length > 1">
								<template #activator>
									<app-icon
										class="project-page__direction-uc-left-icon"
										:style="{ marginTop: '-2px' }"
										name="close-bold"
										width="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
										@click="onDetachClient(ac.Id)"
									/>
								</template>
							</app-tooltip>
							<app-resizable-dropdown
								v-if="projectStore.project?.AttachedClientsAccess==2"
								position="right"
								:initial-width="400"
								:initial-height="450"
								:min-width="400"
								:max-width="800"
								:min-height="450"
								:max-height="900"
								:mobile-title="$t('choose_roles')"
								:ref="(el: any) => { if (el) $addClientRoles[ac.Id] = el }"
							>
								<template #activator>
									<app-tooltip :text="$t('client_add_role')">
										<template #activator>
											<app-icon
												class="project-page__direction-uc-left-icon"
												:style="{ marginTop: '-2px' }"
												name="hash"
												width="18px"
												color="#979797"
												hover-color="#499259"
												cursor="pointer"
											/>
										</template>
									</app-tooltip>
								</template>
								<project-add-role-dropdown
									v-if="project"
									:project-id="project.Id"
									:entity-id="ac.Id"
									:attached-roles="ac.Roles ?? []"
									target="client"
									show-close-icon
									@close="closeAddClientRoleDropdown(ac.Id)"
								/>
							</app-resizable-dropdown>
							<span
								v-if="ac.Roles?.length"
								class="project-page__direction-uc-left-roles"
							>
								<span
									v-for="role in ac.Roles"
									:key="role.Id"
									class="project-page__direction-uc-left-roles-item"
								>
									<span class="project-page__direction-uc-left-roles-item-name">
										{{ role.Name }}
									</span>
									<app-tooltip :text="$t('remove')" v-if="projectStore.project?.AttachedClientsAccess==2">
										<template #activator>
											<app-icon
												class="project-page__direction-uc-left-icon"
												:style="{ marginTop: '-2px' }"
												name="close-bold"
												width="12px"
												color="#D2D2D2"
												hover-color="#BA4C4C"
												cursor="pointer"
												@click="onDetachClientRole(ac.Id, role.Id)"
											/>
										</template>
									</app-tooltip>
								</span>
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="project-page__divider" />

		<div class="project-page__direction">
			<div class="project-page__direction-header">
				<div class="project-page__direction-header-left">
					<app-resizable-dropdown
						v-if="projectStore.project?.AttackedWorkersAccess==2"
						position="right"
						resizable
						:initial-width="400"
						:initial-height="450"
						:min-width="400"
						:max-width="800"
						:min-height="450"
						:max-height="900"
						:mobile-title="$t('choose_workers')"
						ref="$addWorker"
						name="$addWorker"
					>
						<template #activator>
							<app-tooltip :text="$t('ui_add')">
								<template #activator>
									<app-icon
										class="project-page__direction-header-icon"
										name="plus"
										width="15px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>
						<project-add-worker-dropdown
							v-if="project"
							:project-id="project.Id"
							:added-workers="project.AttachedWorkers"
							show-close-icon
							@close="closeAddWorkerDropdown"
						/>
					</app-resizable-dropdown>
					<app-icon
						v-else
						class="project-page__direction-header-icon"
						name="plus"
						width="15px"
						color="#D2D2D2"
					/>
					<div class="project-page__direction-header-title">
						{{ $t('client_workers') }}
					</div>
				</div>
			</div>
			<div
				v-if="project?.AttachedWorkers && project.AttachedWorkers.length && projectStore.project?.AttackedWorkersAccess!==0"
				:class="{
					'project-page__direction-body': true,
					'project-page__direction-body--aw': true
				}"
			>
				<div
					v-for="(aw, index) in project.AttachedWorkers"
					:key="aw.Id"
					class="project-page__direction-aw"
				>
					<div class="project-page__direction-aw-left">
						<div class="project-page__direction-aw-left-count">{{ index + 1 }}.</div>
						<div :class="{
							'project-page__direction-aw-left-name': true,
							'project-page__direction-aw-left-name--removed': aw.Removed,
						}">
							{{ aw.Name }}
							<app-tooltip :text="$t('remove')" v-if="projectStore.project?.AttackedWorkersAccess==2">
								<template #activator>
									<app-icon
										class="project-page__direction-aw-left-icon"
										:style="{ marginTop: '-2px' }"
										name="close-bold"
										width="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
										@click="onDetachWorker(aw.Id)"
									/>
								</template>
							</app-tooltip>
							<app-resizable-dropdown
								v-if="projectStore.project?.AttackedWorkersAccess==2"
								position="right"
								:initial-width="400"
								:initial-height="450"
								:min-width="400"
								:max-width="800"
								:min-height="450"
								:max-height="900"
								:mobile-title="$t('choose_roles')"
								:ref="(el: any) => { if (el) $addWorkerRoles[aw.Id] = el }"
							>
								<template #activator>
									<app-tooltip :text="$t('client_add_role')">
										<template #activator>
											<app-icon
												class="project-page__direction-aw-left-icon"
												:style="{ marginTop: '-2px' }"
												name="hash"
												width="18px"
												color="#979797"
												hover-color="#499259"
												cursor="pointer"
											/>
										</template>
									</app-tooltip>
								</template>
								<project-add-role-dropdown
									v-if="project"
									:project-id="project.Id"
									:entity-id="aw.Id"
									:attached-roles="aw.Roles ?? []"
									target="worker"
									show-close-icon
									@close="closeAddWorkerRoleDropdown(aw.Id)"
								/>
							</app-resizable-dropdown>
							<span
								v-if="aw.Roles?.length"
								class="project-page__direction-aw-left-roles"
							>
								<span
									v-for="role in aw.Roles"
									:key="role.Id"
									class="project-page__direction-aw-left-roles-item"
								>
									<span class="project-page__direction-aw-left-roles-item-name">
										{{ role.Name }}
									</span>
									<app-tooltip :text="$t('remove')" v-if="projectStore.project?.AttackedWorkersAccess==2">
										<template #activator>
											<app-icon
												class="project-page__direction-aw-left-icon"
												:style="{ marginTop: '-2px' }"
												name="close-bold"
												width="12px"
												color="#D2D2D2"
												hover-color="#BA4C4C"
												cursor="pointer"
												@click="onDetachWorkerRole(aw.Id, role.Id)"
											/>
										</template>
									</app-tooltip>
								</span>
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="project-page__divider" />

		<div
			v-if="projectStore.project?.ParameterAccess==1 || projectStore.project?.ParameterAccess==2"
			class="project-page__direction"
		>
			<div class="project-page__direction-header">
				<div class="project-page__direction-header-left">
					<app-tooltip :text="$t('ui_add')" v-if="projectStore.project?.ParameterAccess==2">
						<template #activator>
							<span
								ref="parametersAddAnchor"
								class="project-page__direction-header-add-anchor"
								@click="parametersAddOpen = true"
							>
								<app-icon
									class="project-page__direction-header-icon"
									name="plus"
									width="15px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
								/>
							</span>
						</template>
					</app-tooltip>
					<app-icon
						v-else
						class="project-page__direction-header-icon"
						name="plus"
						width="15px"
						color="#D2D2D2"
					/>
					<div class="project-page__direction-header-title">
						{{ $t('parameters_label') }}
					</div>
				</div>
			</div>
			<div v-if="project" class="project-page__direction-body">
				<parameters-section
					:key="projectId"
					:entity-id="projectId"
					:attachment-type="V2ParameterAttachmentType.Project"
					:parameter-access="projectStore.project?.ParameterAccess"
					:add-menu-target="parametersAddAnchor"
					v-model:add-menu-open="parametersAddOpen"
					@changed="fetchProject"
				/>
			</div>
		</div>
		<div class="project-page__divider" />

		<div class="project-page__direction" style="height: 100%; min-height: 123px;">
			<file-block
				v-if="projectStore.project?.FileAccess==1 || projectStore.project?.FileAccess==2"
				:id="projectId"
				:root-name="project?.Name"
				:rule-access="projectStore.project?.FileAccess"
			/>
		</div>

		<project-info-modal
			ref="$projectInfoModal"
			:title="$t('project_information_and_access')"
		/>
		<stage-business-logic-modal
			ref="$stageBusinessLogicModal"
		/>
		<project-clients-expand-modal ref="$clientsModal" />
		<project-finance-expand-modal
			ref="$financeModal"
			@changed="fetchProject"
		/>
	</div>
	<div class="project-page" v-if="!isProjectLoading && projectStore.project?.FileAccess === 0" style="justify-content: center; margin-top: 8px; max-height: 378px;">
		<div class="project-page__direction">
			<div>
				<div class="project-page__direction-permition">
					<app-icon
						class="project-page__direction-permition-icon"
						name="invalid"
						width="24px"
						color="#979797"
					/>
					<div class="project-page__direction-permition-label">
						{{ $t('project_files_no_access') }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
@import "@/styles/color.scss";

.project-page {
	display: flex;
	flex-direction: column;
	position: relative;
	max-height: 100%;
	height: 100%;
	width: 100%;
	padding: 12px;
	border-radius: 8px;
	background: #fff;
	overflow: auto;

	&:hover :deep(.app-scroll-top--enabled) {
		opacity: 1;
		visibility: visible;
		pointer-events: auto;
	}

	:deep(.app-scroll-top) {
		margin-right: 0;
	}

	@media (min-width: 769px) {
		:deep(.project-page__scroll-top) {
			top: calc(100% - 38px);
			bottom: auto;
		}
	}

	&__header {
		display: flow-root;

		&-permition {
			display: flex;
			flex-direction: row;
			gap: 4px;
			margin-top: 2px;

			&-label {
				color: $color-text-secondary;
				font-size: 14px;
				font-style: normal;
				font-weight: 400;
				line-height: 18px;
			}
		}

		&-options {
			display: block;
			line-height: 20px;

			> * {
				vertical-align: middle;
			}

			> * + * {
				margin-left: 4px;
			}

			&-tags {
				display: inline;

				&-item {
					display: inline-flex;
					align-items: center;
					vertical-align: middle;
					max-width: 100%;
					margin-right: 2px;

					&-name {
						color: #979797;
						margin-right: 2px;
						min-width: 0;
						overflow-wrap: anywhere;
					}
				}
			}
		}

		&-person {
			display: flex;
			align-items: center;
			gap: 4px;
			float: right;
			height: 20px;
			margin-left: 8px;
			max-width: 100%;
			white-space: nowrap;

			&-name,
			&-date {
				font-size: 14px;
				line-height: 18px;
				color: #4D4D4D;
			}

			&-name {
				padding-right: 4px;
				border-right: 1px solid #F2F2F2;
				min-width: 0;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			&-date {
				flex-shrink: 0;
			}

			&-icon {
				margin-top: -3px;
			}
		}
	}

	&__project {
		display: flex;
		gap: 4px;

		&-edit {
			visibility: hidden;
			opacity: 0;
			transition: opacity 0.2s;
		}

		&:hover &-edit {
			visibility: visible;
			opacity: 1;
		}

		@media (hover: none) {
			&-edit {
				visibility: visible;
				opacity: 1;
			}
		}

		&-title,
		&-name {
			display: inline;
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
		}

		&-title {
			font-weight: 500;
		}

		&-name {
			margin-right: 4px;
			font-weight: 400;
		}
	}

	&__stage {
		display: flex;
		align-items: center;
		gap: 4px;

		&-title {
			flex: 1;
			min-width: 0;
			display: flex;
			align-items: center;
			gap: 4px;
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
			font-weight: 500;
		}

		&-select {
			flex: 1;
			min-width: 0;
		}

		&-select-tooltip {
			flex: 1;
			min-width: 0;

			:deep(.app-tooltip__activator) {
				width: 100%;
			}
		}
	}

	&__divider {
		width: 100%;
		border-bottom: 1px solid #F2F2F2;
		margin: 8px 0;
	}

	&__direction {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: fit-content;

		&-permition {
			display: flex;
			align-items: center;
			justify-items: center;
			flex-direction: column;

			&-icon {
				position: relative;
				display: flex;
				flex-direction: column;
			}

			&-label {
				color: var(--Text-color-text-secondary, #979797);
				text-align: center;
				font-size: 16px;
				font-style: normal;
				font-weight: 600;
				line-height: 20px;

			}
		}

		&-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			flex-wrap: wrap;

			&-left,
			&-right {
				display: flex;
				align-items: center;
			}

			&-icon {
				margin-top: -3px;
				margin-right: 4px;
			}

			&-right,
			&-title {
				font-size: 14px;
				line-height: 18px;
				color: #4D4D4D;
			}

			&-title {
				font-weight: 500;
				margin-right: 2px;
				white-space: nowrap;
			}

			&-right {
				margin-left: 19px;

				&-debt {
					color: #D05A4F;
				}

				&-income {
					color: #499259;
				}
			}
		}

		&-body {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: fit-content;
			margin-top: 8px;

			&--uc,
			&--aw {
				gap: 2px;
			}

			&--uc {
				max-height: 220px;
				overflow-y: auto;
			}
		}

		&-uc,
		&-aw {
			display: flex;
			align-items: center;
			justify-content: space-between;

			&-left {
				display: flex;
				align-items: center;
				font-size: 14px;
				line-height: 18px;
				gap: 2px;

				&-count {
					margin: 0 2px 0 3px;
				}

				&-name,
				&-count,
				&-address {
					color: #4D4D4D;
				}

				&-address {
					white-space: nowrap;
				}

				&-name {
					&--removed {
						color: #979797;
					}
				}
			}

			&-right {
				color: #4D4D4D;
			}
		}

		&-uc {
			&-left {
				align-items: flex-start;

				&-name {
					&-str {
						color: #438451;
						cursor: pointer;
						text-decoration: none;

						&:hover {
							text-decoration: underline;
						}
					}
				}

				&-roles {
					display: inline;
					margin-left: 2px;

					&-item {
						display: inline-flex;
						align-items: center;
						vertical-align: middle;
						margin-right: 2px;

						&-name {
							color: #979797;
							margin-right: 2px;
						}
					}
				}
			}
		}

		&-aw {
			&-left {
				align-items: flex-start;

				&-roles {
					display: inline;
					margin-left: 2px;

					&-item {
						display: inline-flex;
						align-items: center;
						vertical-align: middle;
						margin-right: 2px;

						&-name {
							color: #979797;
							margin-right: 2px;
						}
					}
				}
			}
		}
	}
}

.project-page__direction-header--finance {
	flex-wrap: nowrap;
	align-items: flex-start;
}

.project-page__direction-header--finance .project-page__direction-header-left {
	flex-shrink: 0;
}

.project-page__direction-header--finance .project-page__direction-header-right {
	min-width: 0;
	flex-wrap: wrap;
	justify-content: flex-end;
	margin-left: auto;
	column-gap: 12px;
}

@media (max-width: 480px) {
	.project-page {
		padding: 24px 12px 12px;
	}

	.project-page__direction-header--finance .project-page__direction-header-right {
		flex: 1;
		justify-content: flex-start;
	}

	.project-page__direction-header--finance :deep(.finance-summary) {
		display: block;
		width: 100%;
		text-align: right;

		> span {
			display: block;
		}
	}
}

.project-page__direction-header-add-anchor {
	display: inline-flex;
	align-items: center;
	vertical-align: middle;
	cursor: pointer;
	line-height: 0;
}
</style>
